import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Zap, Link2, Unlink, ExternalLink, Loader2, CreditCard, AlertTriangle } from 'lucide-react';
import { userApi, gscApi, billingApi, type SubscriptionInfo } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

type PaidPlanKey = 'starter' | 'pro' | 'business';

const plans = [
  {
    name: 'Free',
    key: 'free',
    monthlyPrice: 0,
    yearlyPrice: 0,
    description: 'Try it out',
    features: ['1 Domain', '2,000 URLs', 'Dashboard only'],
    cta: 'Current Plan',
    highlighted: false,
  },
  {
    name: 'Starter',
    key: 'starter',
    monthlyPrice: 15,
    yearlyPrice: 12,
    description: 'For small sites',
    features: ['2 Domains', '50,000 URLs', 'Full API', 'IndexNow', 'GSC Auto-Submit'],
    cta: 'Upgrade to Starter',
    highlighted: false,
  },
  {
    name: 'Pro',
    key: 'pro',
    monthlyPrice: 50,
    yearlyPrice: 40,
    description: 'For growing sites',
    features: ['5 Domains', '500,000 URLs', 'Full API', 'GSC Integration', 'Priority email support'],
    cta: 'Upgrade to Pro',
    highlighted: true,
  },
  {
    name: 'Business',
    key: 'business',
    monthlyPrice: 150,
    yearlyPrice: 120,
    description: 'For agencies & scale',
    features: ['10 Domains', '5,000,000 URLs', 'Full API', '4h support target'],
    cta: 'Upgrade to Business',
    highlighted: false,
  },
  {
    name: 'Enterprise',
    key: 'enterprise',
    monthlyPrice: null,
    yearlyPrice: null,
    description: 'For large organizations',
    features: ['Unlimited Domains', 'Unlimited URLs', 'Custom SLA', 'Dedicated support'],
    cta: 'Contact Sales',
    highlighted: false,
  },
] as const;

function formatPeriodEnd(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function StatusBadge({ sub }: { sub: SubscriptionInfo | undefined }) {
  if (!sub || sub.plan === 'free') return null;

  if (sub.status === 'active' && sub.cancel_at_period_end) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full text-xs font-medium">
        Cancels {formatPeriodEnd(sub.current_period_end)}
      </span>
    );
  }
  if (sub.status === 'active' || sub.status === 'trialing') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 rounded-full text-xs font-medium">
        <Check className="w-3 h-3" />
        {sub.status === 'trialing' ? 'Trialing' : 'Active'}
      </span>
    );
  }
  if (sub.status === 'past_due') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-700 rounded-full text-xs font-medium">
        <AlertTriangle className="w-3 h-3" />
        Payment past due
      </span>
    );
  }
  if (sub.status === 'canceled') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
        Canceled
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs font-medium capitalize">
      {sub.status}
    </span>
  );
}

export function Settings() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data } = useQuery({
    queryKey: ['user'],
    queryFn: userApi.me,
  });

  const { data: gscStatus, isLoading: gscLoading } = useQuery({
    queryKey: ['gsc-status'],
    queryFn: gscApi.status,
  });

  const { data: subscription } = useQuery({
    queryKey: ['subscription'],
    queryFn: billingApi.subscription,
  });

  const disconnectMutation = useMutation({
    mutationFn: gscApi.disconnect,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gsc-status'] });
    },
  });

  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('year');

  const checkoutMutation = useMutation({
    mutationFn: (plan: PaidPlanKey) => billingApi.checkout(plan, billingInterval),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
  });

  const portalMutation = useMutation({
    mutationFn: () => billingApi.portal(),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
  });

  useEffect(() => {
    const gscConnected = searchParams.get('gsc_connected');
    const gscError = searchParams.get('gsc_error');
    const upgrade = searchParams.get('upgrade');

    if (gscConnected || gscError || upgrade) {
      searchParams.delete('gsc_connected');
      searchParams.delete('gsc_error');
      searchParams.delete('upgrade');
      searchParams.delete('session_id');
      setSearchParams(searchParams, { replace: true });

      queryClient.invalidateQueries({ queryKey: ['gsc-status'] });
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
    }
  }, [searchParams, setSearchParams, queryClient]);

  const currentPlan = subscription?.plan || data?.user?.plan || 'free';
  const hasStripeCustomer = !!subscription?.has_stripe_customer;

  const handleEnterpriseContact = () => {
    window.location.href = 'mailto:sales@sitemaphost.app?subject=Enterprise%20plan%20inquiry';
  };

  const handlePlanClick = (planKey: string) => {
    if (planKey === currentPlan) return;
    if (planKey === 'enterprise') return handleEnterpriseContact();
    if (planKey === 'free') return; // downgrades happen via portal
    checkoutMutation.mutate(planKey as PaidPlanKey);
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-slate-600">Manage your account and subscription</p>
      </div>

      {/* Upgrade success/cancel banner */}
      {searchParams.get('upgrade') === 'success' && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-800">
          Payment received — your plan should refresh momentarily.
        </div>
      )}

      {/* Account Info */}
      <div className="bg-white rounded-xl border shadow-sm p-6 mb-8">
        <h2 className="font-semibold mb-4">Account Information</h2>
        {data?.user && (
          <div className="space-y-3">
            <div>
              <label className="text-sm text-slate-500">Email</label>
              <p className="font-medium">{data.user.email}</p>
            </div>
            <div>
              <label className="text-sm text-slate-500">Name</label>
              <p className="font-medium">{data.user.name || 'Not set'}</p>
            </div>
            <div>
              <label className="text-sm text-slate-500">Current Plan</label>
              <p className="font-medium capitalize flex items-center gap-2">
                {currentPlan}
                <StatusBadge sub={subscription} />
              </p>
            </div>
          </div>
        )}

        {hasStripeCustomer && (
          <div className="mt-6 pt-6 border-t">
            <button
              onClick={() => portalMutation.mutate()}
              disabled={portalMutation.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              {portalMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CreditCard className="w-4 h-4" />
              )}
              Manage Subscription &amp; Billing
            </button>
            <p className="text-xs text-slate-500 mt-2">
              Update card, change plan, download invoices, or cancel.
            </p>
          </div>
        )}
      </div>

      {/* Google Search Console Integration */}
      <div className="bg-white rounded-xl border shadow-sm p-6 mb-8">
        <h2 className="font-semibold mb-4 flex items-center gap-2">
          <Link2 className="w-5 h-5" />
          Google Search Console Integration
        </h2>
        <p className="text-sm text-slate-600 mb-4">
          Connect your Google Search Console account to automatically submit sitemaps when they're generated.
        </p>

        {gscLoading ? (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Checking connection status...
          </div>
        ) : gscStatus?.connected ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-green-600">
              <Check className="w-5 h-5" />
              <span className="font-medium">Connected to Google Search Console</span>
            </div>
            <p className="text-sm text-slate-600">
              Sitemaps will be automatically submitted to GSC when you generate or update them.
            </p>
            <button
              onClick={() => disconnectMutation.mutate()}
              disabled={disconnectMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
            >
              {disconnectMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Unlink className="w-4 h-4" />
              )}
              Disconnect
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-sm text-slate-500">Not connected</div>
            <button
              onClick={() => (window.location.href = gscApi.getConnectUrl())}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Connect Google Search Console
            </button>
          </div>
        )}
      </div>

      {/* Pricing */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="font-semibold">Subscription Plans</h2>
          <div className="inline-flex items-center gap-2 bg-slate-100 rounded-lg p-1 text-sm">
            <button
              type="button"
              onClick={() => setBillingInterval('month')}
              className={cn(
                'px-3 py-1 rounded-md transition-colors',
                billingInterval === 'month' ? 'bg-white shadow-sm font-medium' : 'text-slate-600 hover:text-slate-900'
              )}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setBillingInterval('year')}
              className={cn(
                'px-3 py-1 rounded-md transition-colors flex items-center gap-1.5',
                billingInterval === 'year' ? 'bg-white shadow-sm font-medium' : 'text-slate-600 hover:text-slate-900'
              )}
            >
              Yearly
              <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full font-semibold">
                -20%
              </span>
            </button>
          </div>
        </div>
        {checkoutMutation.isError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {(checkoutMutation.error as Error)?.message || 'Could not start checkout. Please try again.'}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {plans.map((plan) => {
            const isCurrent = plan.key === currentPlan;
            const isLoading = checkoutMutation.isPending && checkoutMutation.variables === plan.key;
            const displayPrice = billingInterval === 'year' ? plan.yearlyPrice : plan.monthlyPrice;
            return (
              <div
                key={plan.name}
                className={cn(
                  'rounded-xl border p-6',
                  plan.highlighted
                    ? 'border-primary bg-primary/5 ring-2 ring-primary'
                    : 'bg-white'
                )}
              >
                {plan.highlighted && (
                  <div className="flex items-center gap-1 text-primary text-sm font-medium mb-2">
                    <Zap className="w-4 h-4" />
                    Most Popular
                  </div>
                )}
                <h3 className="text-xl font-bold">{plan.name}</h3>
                <div className="mt-2 mb-4">
                  {displayPrice !== null ? (
                    <>
                      <span className="text-3xl font-bold">${displayPrice}</span>
                      <span className="text-slate-500">/month</span>
                      {billingInterval === 'year' && displayPrice > 0 && (
                        <div className="text-xs text-slate-500 mt-1">
                          ${displayPrice * 12} billed yearly
                        </div>
                      )}
                    </>
                  ) : (
                    <span className="text-3xl font-bold">Custom</span>
                  )}
                </div>
                <p className="text-slate-600 text-sm mb-4">{plan.description}</p>
                <ul className="space-y-2 mb-6">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <button
                  disabled={isCurrent || isLoading || plan.key === 'free'}
                  onClick={() => handlePlanClick(plan.key)}
                  className={cn(
                    'w-full py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2',
                    isCurrent || plan.key === 'free'
                      ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      : plan.highlighted
                        ? 'bg-primary text-white hover:bg-primary/90'
                        : 'border hover:bg-slate-50'
                  )}
                >
                  {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isCurrent ? 'Current Plan' : plan.cta}
                </button>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-slate-500 mt-4">
          Prices in USD. Manage your subscription, update card, or cancel any time via the billing portal above.
        </p>
      </div>

      {/* Danger Zone */}
      <div className="bg-white rounded-xl border border-red-200 p-6">
        <h2 className="font-semibold text-red-600 mb-4">Danger Zone</h2>
        <p className="text-sm text-slate-600 mb-4">
          To close your account, please first cancel any active subscription via the billing portal, then{' '}
          <a href="mailto:support@sitemaphost.app?subject=Account%20deletion%20request" className="underline">
            contact support
          </a>{' '}
          and we'll remove your data within 7 days.
        </p>
      </div>
    </div>
  );
}
